import { first } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { Button, Text } from 'src/@vymo/ui/atoms';
import Divider from 'src/@vymo/ui/atoms/divider';
import { getGrowthBookFeatureFlag } from 'src/featureFlags';
import { Keys, locale } from 'src/i18n';
import keys from 'src/i18n/keys';
import { setAppHideConfig } from 'src/models/appConfig/slice';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { getApiStatus, getCTAs, getVoName } from '../selectors';
import {
  setApiStatus,
  setCurrentCta,
  setShowAppBanner,
  setTemplateUi,
} from '../slice';
import { useStepperContext } from '../stepperProvider';
import { saveAction } from '../thunk';
import ActiveLob from './activeLob';
import Header from './header';
import Lob from './lob';
import NavLob from './navLob';
import RecommendLob from './recommendLob';
import { LobSelectProp, Props } from './types';
import styles from './index.module.scss';

function MultiLob({
  first_time_login,
  ongoing_lob_cards = [],
  recommended_lob_cards = [],
  active_lob_cards = [],
  create_application_options,
  completed_application_cards = [],
}: Props) {
  const { isDialog, debugMode } = useStepperContext();
  const isEntityPayloadDisabled = getGrowthBookFeatureFlag(
    'disable-entity-payload',
  );
  const apiStatus = useAppSelector((state) => getApiStatus(state, isDialog));
  const [selectedLob, setSelectedLob] = useState<Array<LobSelectProp>>([]);
  const dispatch = useAppDispatch();
  const [showChooseInterest, setShowChooseInterest] = useState(
    ongoing_lob_cards.length === 0,
  );
  const cta = first(useAppSelector((state) => getCTAs(state, isDialog)));
  const applicantName = useAppSelector(getVoName);

  useEffect(() => {
    if (first_time_login && ongoing_lob_cards.length === 0) {
      setShowChooseInterest(true);
    } else {
      setShowChooseInterest(false);
    }
  }, [dispatch, first_time_login, ongoing_lob_cards.length]);

  useEffect(
    // @ts-ignore
    () => () => {
      dispatch(setTemplateUi({ isDialog, templateUi: {} }));
      dispatch(setShowAppBanner(false));
      dispatch(
        setAppHideConfig({
          hide: {
            headerHamburger: false,
          },
        }),
      );
    },
    [dispatch, isDialog],
  );

  useEffect(() => {
    if (!showChooseInterest) {
      dispatch(setShowAppBanner(true));
      dispatch(
        setTemplateUi({
          isDialog,
          templateUi: {
            header: { hide: { title: true, description: true } },
            footer: { hide: { cta: true } },
          },
        }),
      );
      dispatch(
        setAppHideConfig({
          hide: {
            headerHamburger: false,
          },
        }),
      );
    } else {
      setSelectedLob([]);
      dispatch(setShowAppBanner(false));
      dispatch(setTemplateUi({ isDialog, templateUi: {} }));
      dispatch(
        setAppHideConfig({
          hide: {
            headerHamburger: true,
          },
        }),
      );
    }
  }, [dispatch, isDialog, showChooseInterest]);

  useEffect(() => {
    if (apiStatus === 'cta_clicked') {
      dispatch(
        // @ts-ignore
        saveAction({
          payload: {
            lobs: isEntityPayloadDisabled
              ? selectedLob.map((lob) => lob.entity)
              : selectedLob,
          },
          isDialog,
          debugMode,
        }),
      );
    }
  }, [
    apiStatus,
    debugMode,
    dispatch,
    isDialog,
    isEntityPayloadDisabled,
    selectedLob,
  ]);

  const onNewLobItemClick = useCallback(
    (lobProp: LobSelectProp) => {
      if (cta && lobProp) {
        setSelectedLob((prev) =>
          prev.some((item) => item.entity === lobProp.entity)
            ? prev
            : [...prev, lobProp],
        );
        dispatch(setApiStatus({ isDialog, apiStatus: 'cta_clicked' }));
        dispatch(setCurrentCta({ isDialog, currentCta: cta }));
      }
    },
    [cta, dispatch, isDialog],
  );

  return (
    <div className={styles.multiLob}>
      <Header
        name={applicantName}
        ctaLabel={locale(keys.CREATE_NEW_APPLICATION)}
        options={create_application_options?.options}
        onNewLobItemClick={onNewLobItemClick}
        isCreateNewEnabled={create_application_options?.enabled}
      />
      <Divider />
      {showChooseInterest ? (
        <div
          className={styles.multiLob__ongoing}
          data-test-id="multiLob-choose"
        >
          {recommended_lob_cards.map((lob) => (
            <Lob
              {...lob}
              selectedLob={selectedLob}
              setSelectedLob={setSelectedLob}
            />
          ))}
        </div>
      ) : (
        <>
          <Text type="h4" data-test-id="multiLob-ongoing">
            {locale(Keys.ONBOARDING_IN_PROGRESS_COUNT, {
              count: ongoing_lob_cards.length,
            })}
          </Text>
          <div className={styles.multiLob__ongoing}>
            {ongoing_lob_cards.map((lob) => (
              <NavLob {...lob} />
            ))}
          </div>

          {recommended_lob_cards.length > 0 && (
            <div className={styles.multiLob__recommended__card}>
              <Text type="h4" data-test-id="multiLob-recommend">
                {locale(Keys.RECOMMENDED_FOR_YOU)}
              </Text>
              <div className={styles.multiLob__recommended}>
                {recommended_lob_cards.slice(0, 2).map((lob) => (
                  <RecommendLob
                    setShowChooseInterest={setShowChooseInterest}
                    {...lob}
                  />
                ))}
              </div>
              {recommended_lob_cards.length > 2 && (
                <Button
                  type="text"
                  size="small"
                  onClick={() => setShowChooseInterest(true)}
                >
                  {locale(Keys.VIEW_ALL)}
                </Button>
              )}
            </div>
          )}

          {active_lob_cards.length > 0 && (
            <>
              <Text type="h4">{locale(Keys.ACTIVE_BUSINESSES)}</Text>
              <div className={styles.multiLob__recommended}>
                {active_lob_cards.map((lob) => (
                  <ActiveLob {...lob} />
                ))}
              </div>
            </>
          )}

          {completed_application_cards.length > 0 && (
            <>
              <Text type="h4">
                {locale(Keys.COMPLETED_APPLICATIONS, {
                  count: completed_application_cards.length,
                })}
              </Text>

              {completed_application_cards.map((lob) => (
                <NavLob {...lob} isCompleted />
              ))}
            </>
          )}
        </>
      )}
    </div>
  );
}

export default MultiLob;
